import { gql } from '@soundxyz/gql-string';
import type { FragmentType, PlaylistInput } from '@graphql/reactQuery';
import { getFragment, ReleasesCardInfoFragmentDoc } from '@graphql/reactQuery';
import { usePlayButton } from '@hooks/usePlayButton';
import { useAudioController } from '@store/audio/AudioController';
import { getTrackUrl } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleasesCardInfo on Release {
    id
    type
    externalUrl
    title
    webappUri
    artist {
      id
      name
      webappUri
      userId
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    track {
      id
    }
  }
`);

/**
 * All the information needed to power any song card
 */
export const useReleaseCardInfo = ({
  data,
  playlist,
}: {
  data: FragmentType<ReleasesCardInfoFragmentDoc>;
  playlist?: PlaylistInput;
}) => {
  const { activeTrackId, playing } = useAudioController();

  const release = getFragment(ReleasesCardInfoFragmentDoc, data);

  const coverImageUrl = release.staticCoverImage?.url || release.coverImage.url;

  const isAlbum = release.type === 'ALBUM';

  const isActiveTrack = !!activeTrackId && activeTrackId === release.track?.id && playing;
  const trackUrl = getTrackUrl({
    isAlbum,
    externalUrl: release.externalUrl,
    webappUri: release.webappUri,
  });

  const { onPlayClick, isLoading, ref } = usePlayButton({
    isAlbum,
    trackId: release.track.id,
    playlist,
  });
  const hasAudioAccess = release.type !== 'ALBUM';

  return {
    /**
     * URL for release page or external URL if album
     */
    trackUrl,
    /**
     * Wether this song is actively playing?
     */
    isActiveTrack,
    /**
     * Cover image to show for the release.
     *
     * Tries the optimized one and then fallbacks to default
     */
    coverImageUrl,
    /**
     * Play button click handler
     */
    onPlayClick,
    /**
     * Does the user have access to the audio?
     *
     * For albums we will disable the play button. We will re-consider what to do in future.
     */
    hasAudioAccess: isAlbum ? false : hasAudioAccess,
    /**
     * Ref the component so it can prefetches audio when in view
     */
    playButtonPrefetchRef: ref,
    /**
     * Are we trying to load the song?
     */
    isAudioBuffering: isLoading,
    /**
     * User ID of the artist
     */
    artistUserId: release.artist.userId,
    /**
     * ID of the artist
     */
    artistId: release.artist.id,
    /**
     * Name of the artist
     */
    artistName: release.artist.name,
    /**
     * Path for artist page on Sound
     */
    artistWebappPath: webappUriPath(release.artist.webappUri),
    /**
     * Name of the song
     */
    releaseTitle: release.title,
    /**
     * Is it an album?
     */
    isAlbum: release.type === 'ALBUM',
  };
};
